import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import RotatingImage from '../RotatingImage';
import CapacityBadge from '../CapacityBadge';
import MarkdownRenderer from '../MarkdownRenderer';
import ImageGallery from '../ImageGallery';
import { getApiBaseUrl } from '../../api/client';
import { sortSchedule, weekdayName, recurrenceLabel } from '../../utils/courseSchedule';
import CollapsibleWidget from '../CollapsibleWidget';
import CoursePricingPanel from './CoursePricingPanel';
import { formatPrice, lowestPrice, type IPricingSelection } from '../../utils/coursePricing';
import { seasonTermLabel } from '../../utils/seasonLabel';
import type { ICourse } from '../../utils/courseSchedule';
import type { CourseMemberState } from './CourseRow';

interface CourseDetailProps {
  course: ICourse;
  /** The currently selected member, or null when none chosen / logged out. */
  selectedMemberId: number | null;
  /** This member's enrollment state for this course. */
  state: CourseMemberState;
  submitting: boolean;
  /** `selection` carries a season stretch picked in the pricing modal. */
  onJoin: (course: ICourse, selection?: IPricingSelection) => void;
  onLeave: (course: ICourse) => void;
  /**
   * When true, this member just joined a paid open-enrollment course — show the
   * informational "complete payment" banner with the Pay-now bridge.
   */
  showPayPrompt?: boolean;
  /** Bridge to the choose-payment surface (member memberships page). */
  onGoToPayment?: () => void;
}

/**
 * Resolve a possibly-relative image URL against the API base, mirroring the
 * helper used by PublicEventPage so gallery thumbnails load correctly.
 */
const getImageUrl = (url: string): string =>
  url.startsWith('/') ? `${getApiBaseUrl()}${url}` : url;

/**
 * Full course (kroužek) detail rendered inside a nested modal opened over the
 * activity detail modal. Shows large media (gallery lightbox when available,
 * else the rotating cover), the enrollment-policy badge + capacity, the full
 * weekly schedule (every entry, day + time + location), the full markdown
 * description, and the per-member join action. The course name + close live in
 * the wrapping modal header; the member selector stays in the activity modal,
 * so this view receives the resolved `selectedMemberId`/`state`/handlers.
 */
const CourseDetail: React.FC<CourseDetailProps> = ({
  course,
  selectedMemberId,
  state,
  submitting,
  onJoin,
  onLeave,
  showPayPrompt = false,
  onGoToPayment,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const primaryColor = course.colorSettings?.primaryColor || 'var(--orange-primary)';
  const secondaryColor = course.colorSettings?.secondaryColor || primaryColor;

  const detailStyle = {
    '--event-primary': primaryColor,
    '--event-secondary': secondaryColor,
  } as React.CSSProperties;

  // null when nothing on the course is priced — then no pricing section at all.
  const pricingFrom = lowestPrice(course);

  const enrolledCount = course.enrolledCount ?? 0;
  const isFull = course.capacity != null && enrolledCount >= course.capacity;

  const gallery = course.gallery && course.gallery.length > 0 ? course.gallery : null;
  const sortedSchedule = sortSchedule(course.schedule || []);
  const hasTerm = !!(course.termStart || course.termEnd);
  const season = hasTerm ? seasonTermLabel(course.termStart, course.termEnd, t, locale) : null;
  const descriptionContent =
    course.extendedDescription || course.description || '';

  return (
    <div className="course-detail" style={detailStyle}>
      <div className="course-detail__media">
        {gallery ? (
          <ImageGallery
            images={gallery}
            getImageUrl={getImageUrl}
            altPrefix={course.name}
          />
        ) : (
          <div className="course-detail__cover">
            <RotatingImage
              coverImage={course.coverImage}
              gallery={null}
              seed={String(course._id)}
              alt={course.name}
            />
          </div>
        )}
      </div>

      <div className="course-detail__header">
        <span
          className={`course-pcard__policy-badge course-pcard__policy-badge--${course.enrollmentPolicy} course-detail__policy-badge`}
        >
          {course.enrollmentPolicy === 'open'
            ? t('courses.enrollmentPolicyOpen')
            : t('courses.enrollmentPolicyApproval')}
        </span>
        <CapacityBadge enrolled={enrolledCount} capacity={course.capacity} />
        {season && <span className="course-detail__season">{season}</span>}
      </div>

      {sortedSchedule.length > 0 ? (
        <ul className="course-detail__schedule">
          {sortedSchedule.map((entry, i) => (
            <li key={i} className="course-detail__schedule-row">
              <span className="course-pcard__day-chip">
                {weekdayName(entry.dayOfWeek, locale)}
              </span>
              <span className="course-detail__time">
                {entry.startTime}–{entry.endTime}
              </span>
              <span className="course-detail__recurrence">
                ({recurrenceLabel(entry.recurringPattern, t)})
              </span>
              {entry.location && (
                <span className="course-detail__location">📍 {entry.location}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="course-detail__no-schedule">{t('courses.noSchedule')}</p>
      )}

      {/* Pricing inline in a collapsible section rather than behind the card's
          price badge: a modal opened from inside this (already nested) detail
          modal is easy to miss, and the badge gives no hint it is clickable. The
          "from X" price stays in the header so it reads while collapsed. Content
          is the same CoursePricingPanel the badge's modal renders, so the two
          surfaces cannot drift. */}
      {pricingFrom !== null && (
        <div className="course-detail__pricing">
          <CollapsibleWidget
            title={t('courses.pricingFromTitle', {
              price: formatPrice(pricingFrom, locale),
            })}
            defaultExpanded={false}
            storageKey="course-detail-pricing"
          >
            <CoursePricingPanel
              course={course}
              selectedMemberId={selectedMemberId}
              state={state}
              isFull={isFull}
              submitting={submitting}
              onChoose={onJoin}
              showHeader={false}
            />
          </CollapsibleWidget>
        </div>
      )}

      {descriptionContent && (
        <div className="course-detail__description">
          <MarkdownRenderer content={descriptionContent} />
        </div>
      )}

      {showPayPrompt && state === 'enrolled' && onGoToPayment && (
        <div className="course-detail__pay-prompt">
          <span className="course-detail__pay-prompt-text">{t('courses.payPrompt')}</span>
          <Button variant="primary" fullWidth onClick={onGoToPayment}>
            {t('courses.payNow')}
          </Button>
        </div>
      )}

      <div className="course-detail__action">
        {selectedMemberId != null ? (
          state === 'enrolled' ? (
            <>
              <span className="course-pcard__badge course-pcard__badge--enrolled">
                {t('courses.enrolled')}
              </span>
              <Button
                variant="danger"
                fullWidth
                onClick={() => onLeave(course)}
                loading={submitting}
                disabled={submitting}
              >
                {t('courses.leave')}
              </Button>
            </>
          ) : state === 'pending' ? (
            <span className="course-pcard__badge course-pcard__badge--pending">
              {t('courses.pending')}
            </span>
          ) : (
            <>
              {season && (
                <p className="course-detail__join-scope">
                  {t('courses.joiningForSeason')}{' '}
                  <strong className="course-detail__join-scope-term">{season}</strong>
                </p>
              )}
              <button
                type="button"
                className="course-pcard__join-cta"
                onClick={() => onJoin(course)}
                disabled={submitting || isFull}
                title={isFull ? t('courses.full') : undefined}
              >
                {isFull
                  ? t('courses.full')
                  : submitting
                  ? `${t('courses.join')}…`
                  : t('courses.join')}
              </button>
            </>
          )
        ) : (
          <p className="course-detail__pick-member">{t('courses.joiningAs')}</p>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
